/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from '../api/axiosInstance';
import { useNavigate, useSearchParams } from "react-router-dom";
import '../ReservationsList.css';
import * as XLSX from "xlsx"; // Importamos la librería
import palantirEye from "../assets/logo-palantir-no-bg.png";
import OccupancyGrid from "../Components/OccupancyGrid";
import { isNonOccupyingReservation } from "../reservations/reservationOccupancy";
import { getBookerDisplayName, getBookerContactName, getReservationCustomerName, isCompanyBooker } from "../reservations/bookerPresentation";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import useCrossTabDataRefreshToken from "../hooks/useCrossTabDataRefresh";
import { translateReservationStatus } from "../utils/pmsLanguages";
import { readStoredUser } from "../utils/sessionUser";
import { DEMO_BUSINESS_UNIT_ID, isDemoMode } from "../demo/demoMode";
import { demoReferenceDate } from "../demo/demoSnapshot";
import { FiDownload, FiEye, FiEyeOff, FiFilter, FiGrid, FiPlus, FiSearch } from "react-icons/fi";

const getDefaultReservationColumnFilters = () => ({
    locator: "",
    externalLocator: "",
    customer: "",
    roomCategory: [],
    room: [],
    rate: [],
    checkIn: [],
    checkOut: [],
    roomNights: [],
    adults: [],
    children: [],
    babies: [],
    status: [],
    totalPrice: "",
    externalAgency: [],
    createdAt: "",
});

const RES_LIST_DEFAULT_PAGE_SIZE = 100;
const RES_LIST_PAGE_SIZE_OPTIONS = [50, 100, 200];
const RES_LIST_FILTER_KEYS = [
    "locator",
    "externalLocator",
    "customer",
    "roomCategory",
    "room",
    "rate",
    "checkIn",
    "checkOut",
    "roomNights",
    "adults",
    "children",
    "totalPrice",
    "status",
    "externalAgency",
    "createdAt",
];

const getReservationDisplayLocator = (reservation) => {
    const locator = String(reservation?.locator ?? "").trim();
    const groupLocator = String(reservation?.group_locator ?? "").trim();
    const groupIndex = Number(reservation?.group_index);
    const hasGroupIndex = Number.isFinite(groupIndex) && groupIndex >= 0;

    if (locator && (!groupLocator || locator !== groupLocator || !hasGroupIndex)) {
        return locator;
    }

    if (groupLocator && hasGroupIndex) {
        return `${groupLocator}-${groupIndex + 1}`;
    }

    if (locator) return locator;
    if (groupLocator) return groupLocator;
    return reservation?.id ? `R-${reservation.id}` : "";
};

function amountShapeToNumber(value) {
    if (value && typeof value === "object" && value.amount != null) {
        const amount = Number(value.amount);
        return Number.isFinite(amount) ? amount : 0;
    }

    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
}

function buildPaidMapFromGroupBalances(reservations, balancesByGroupId) {
    return (reservations || []).reduce((acc, reservation) => {
        const reservationId = Number(reservation?.id);
        const groupId = Number(reservation?.reservation_group_id);
        if (!reservationId || !groupId) return acc;

        const groupBalance = balancesByGroupId?.[groupId] || null;
        const balanceRows = Array.isArray(groupBalance?.reservations) ? groupBalance.reservations : [];
        const reservationBalance = balanceRows.find((row) => Number(row.reservation_id) === reservationId);

        if (reservationBalance) {
            acc[reservationId] = amountShapeToNumber(reservationBalance.allocated_paid);
        }

        return acc;
    }, {});
}

const RES_LIST_PROFILE_ENABLED =
    String(process.env.REACT_APP_PROFILE_RESERVATIONS_LIST || "").trim() === "1";

const nowMs = () =>
    (typeof performance !== "undefined" && typeof performance.now === "function")
        ? performance.now()
        : Date.now();

const makeReservationsUiProfiler = (scope, meta = {}) => {
    if (!RES_LIST_PROFILE_ENABLED) {
        return { mark: () => {}, end: () => {} };
    }

    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const start = nowMs();
    let last = start;

    return {
        mark(step, extra = {}) {
            const t = nowMs();
            console.log(
                `[PROFILE][RES_LIST_FE][${id}] ${scope}.${step} +${(t - last).toFixed(1)}ms total=${(t - start).toFixed(1)}ms`,
                { scope, ...meta, ...extra }
            );
            last = t;
        },
        end(extra = {}) {
            const t = nowMs();
            console.log(
                `[PROFILE][RES_LIST_FE][${id}] ${scope}.end total=${(t - start).toFixed(1)}ms`,
                { scope, ...meta, ...extra }
            );
        },
    };
};




function Reservations() {
    console.log("Renderizando Reservations.js");
    const demoReadOnlyActive = isDemoMode();
    const { languageId, language } = usePmsTheme();
    const { convertFromBase, displayCurrency, formatFromBase } = useCurrencyDisplay();
    const commonCopy = language.messages.common;
    const reservationsCopy = language.messages.reservations;
    const [reservations, setReservations] = useState([]);
    const [gridSourceReservations, setGridSourceReservations] = useState([]);
    const [paidMap, setPaidMap] = useState({});
    const [showCancelled, setShowCancelled] = useState(false);
    const [viewMode, setViewMode] = useState("list"); // "list" | "grid"
    const [roomBlocks, setRoomBlocks] = useState([]);
    const navigate = useNavigate();
    const [roomCountsByCategory, setRoomCountsByCategory] = useState({});
    const [roomCategoryNamesById, setRoomCategoryNamesById] = useState({});
    const [roomNumbersByCategory, setRoomNumbersByCategory] = useState({});
    const [roomCatalogById, setRoomCatalogById] = useState({});
    const [groupBookerNamesByGroupId, setGroupBookerNamesByGroupId] = useState({});
    const [isLoadingGridData, setIsLoadingGridData] = useState(false);
    const [searchParams] = useSearchParams();
    const businessUnitId = demoReadOnlyActive
        ? DEMO_BUSINESS_UNIT_ID
        : searchParams.get("business_unit_id");
    const requestedViewMode = searchParams.get("view");
    const requestedGridViewMode = searchParams.get("grid_view");
    const requestedGridStartDate = searchParams.get("date_from") || "";
    const highlightedReservationId = searchParams.get("highlight_reservation_id") || "";
    const currentUser = readStoredUser();
    const permissions = currentUser?.permissions || {};
    const isAdminRole = Number(currentUser?.id) === 1;
    const canViewReservationsList = Boolean(
        isAdminRole || permissions["reservations.view_list"]
    );
    const canCreateReservations = Boolean(
        isAdminRole || permissions["reservations.create"]
    );
    const canViewRoomAssignment = Boolean(
        isAdminRole || permissions["rooms.view_assignment"]
    );
    const canRenderCurrentView =
        canViewReservationsList ||
        canCreateReservations ||
        (viewMode === "grid" && canViewRoomAssignment);
    const canViewReservationDetails = Boolean(permissions["reservations.view_detail"]);
    const canViewPayments = Boolean(permissions["reservations.view_payments"]);

    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [columnFilters, setColumnFilters] = useState(getDefaultReservationColumnFilters);
    const [activeFilter, setActiveFilter] = useState(null); // { key, rect }
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [filterOptionQuery, setFilterOptionQuery] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(RES_LIST_DEFAULT_PAGE_SIZE);
    const [totalRows, setTotalRows] = useState(0);
    const [serverFilterOptions, setServerFilterOptions] = useState(null);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const filterPopoverRef = useRef(null);
    const crossTabRefreshToken = useCrossTabDataRefreshToken();

    const isCancelledReservation = (r) => {
        const s = String(r?.status || "").trim().toLowerCase();
        return s === "cancelled" || s === "canceled" || r?.is_cancelled === true;
        };

    useEffect(() => {
        if (requestedViewMode === "grid") {
            setViewMode("grid");
        } else if (requestedViewMode === "list") {
            setViewMode("list");
        }
    }, [requestedViewMode]);

        const visibleReservations = showCancelled
        ? reservations
        : (reservations || []).filter(r => !isCancelledReservation(r));

        // Cancelled and No Show reservations retain their history without occupying inventory.
        const gridReservations = (gridSourceReservations || []).filter(r => !isNonOccupyingReservation(r));

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 250);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        setPage(1);
    }, [businessUnitId, debouncedSearchQuery, columnFilters, showCancelled, pageSize]);

    useEffect(() => {
        if (!canViewReservationsList) {
            setIsLoadingList(false);
            setReservations([]);
            setTotalRows(0);
            return undefined;
        }
        if (!businessUnitId) {
            setIsLoadingList(false);
            return undefined;
        }

        let isActive = true;
        setIsLoadingList(true);
        const controller = new AbortController();
        const profile = makeReservationsUiProfiler("load_list", { business_unit_id: businessUnitId });
        const isCanceledError = (err) =>
            err?.code === "ERR_CANCELED" ||
            err?.name === "CanceledError" ||
            err?.message === "canceled";

        console.log(`🔍 Loading reservations for ${businessUnitId}...`);
        profile.mark("start");

        (async () => {
            try {
                const params = new URLSearchParams();
                params.set("business_unit_id", String(businessUnitId));
                params.set("page", String(page));
                params.set("page_size", String(pageSize));
                params.set("show_cancelled", showCancelled ? "1" : "0");
                if (debouncedSearchQuery.trim()) {
                    params.set("q", debouncedSearchQuery.trim());
                }
                params.set("filters", JSON.stringify(columnFilters));

                const response = await axios.get(
                    `/api/reservations?${params.toString()}`,
                    { signal: controller.signal }
                );
                if (!isActive) return;

                console.log("📡 Reservas recibidas:", response.data);
                const payload = response?.data;
                const list = Array.isArray(payload)
                    ? payload
                    : (Array.isArray(payload?.rows) ? payload.rows : []);
                const total = Array.isArray(payload)
                    ? list.length
                    : Number(payload?.total ?? list.length);
                const totalPages = Array.isArray(payload)
                    ? 1
                    : Number(payload?.total_pages ?? (total > 0 ? Math.ceil(total / pageSize) : 1));
                const options = Array.isArray(payload)
                    ? null
                    : (payload?.filter_options || null);

                if (!Array.isArray(payload) && page > totalPages && totalPages > 0) {
                    setPage(totalPages);
                    return;
                }

                setReservations(list);
                setTotalRows(Number.isFinite(total) ? total : list.length);
                setServerFilterOptions(options);

                profile.mark("api.reservations", {
                    rows: list.length,
                    total: Number.isFinite(total) ? total : list.length,
                    page,
                    page_size: pageSize,
                });
                if (!isActive) return;

                const fallbackPaidMap = list.reduce((acc, r) => {
                    const id = r?.id;
                    if (!id) return acc;
                    acc[id] = Number(r?.paid_amount || 0);
                    return acc;
                }, {});

                if (canViewPayments && demoReadOnlyActive) {
                    setPaidMap(fallbackPaidMap);
                    profile.mark("build.paid_map", {
                        keys: Object.keys(fallbackPaidMap).length,
                        source: "demo_snapshot",
                    });
                } else if (canViewPayments) {
                    const groupIds = [...new Set(
                        list
                            .map((reservation) => Number(reservation?.reservation_group_id))
                            .filter((groupId) => Number.isInteger(groupId) && groupId > 0)
                    )];

                    if (groupIds.length) {
                        try {
                            const balanceRes = await axios.post(
                                "/api/reservation-groups/payment-balances/batch",
                                { reservation_group_ids: groupIds },
                                { signal: controller.signal, skipCrossTabSync: true }
                            );
                            if (!isActive) return;

                            const balancePaidMap = buildPaidMapFromGroupBalances(
                                list,
                                balanceRes?.data?.balances || {}
                            );
                            setPaidMap({ ...fallbackPaidMap, ...balancePaidMap });
                            console.log("[RES-LIST][Paid][Map] balance ready", balancePaidMap);
                            profile.mark("build.paid_map", { keys: Object.keys(balancePaidMap).length, source: "payment_balance" });
                        } catch (balanceError) {
                            if (isCanceledError(balanceError)) return;
                            console.warn("[RES-LIST][Paid][Map][WARN]", balanceError?.response?.data || balanceError?.message || balanceError);
                            setPaidMap(fallbackPaidMap);
                            profile.mark("build.paid_map", { keys: Object.keys(fallbackPaidMap).length, source: "fallback_paid_amount" });
                        }
                    } else {
                        setPaidMap(fallbackPaidMap);
                        profile.mark("build.paid_map", { keys: Object.keys(fallbackPaidMap).length, source: "fallback_no_groups" });
                    }
                } else {
                    setPaidMap({});
                    profile.mark("build.paid_map", { keys: 0, source: "no_permission" });
                }
                profile.end({ status: "ok" });

            } catch (error) {
                if (isCanceledError(error)) return;
                console.error("❌ Error obteniendo reservas:", error);
                profile.end({ status: "error", message: error?.message || "unknown_error" });
            } finally {
                if (isActive) setIsLoadingList(false);
            }
        })();

        return () => {
            isActive = false;
            controller.abort();
        };
    }, [businessUnitId, page, pageSize, debouncedSearchQuery, columnFilters, showCancelled, canViewPayments, canViewReservationsList, crossTabRefreshToken, demoReadOnlyActive]);

    useEffect(() => {
        if (!businessUnitId) return;
        if (viewMode !== "grid") return;

        let isActive = true;
        const controller = new AbortController();
        const profile = makeReservationsUiProfiler("load_grid_data", { business_unit_id: businessUnitId });
        const isCanceledError = (err) =>
            err?.code === "ERR_CANCELED" ||
            err?.name === "CanceledError" ||
            err?.message === "canceled";
        const pageSizeForGrid = 500;
        const reservationPageConcurrency = 6;
        const reservationGroupFetchConcurrency = 6;
        const extractReservationRows = (payload) =>
            Array.isArray(payload)
                ? payload
                : (Array.isArray(payload?.rows) ? payload.rows : []);
        const extractReservationTotalPages = (payload) => {
            const parsed = Array.isArray(payload) ? 1 : Number(payload?.total_pages ?? 1);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
        };

        setIsLoadingGridData(true);
        setGridSourceReservations([]);
        setGroupBookerNamesByGroupId({});
        profile.mark("start");

        const fetchReservationsPage = async (pageNumber) => {
            const params = new URLSearchParams();
            params.set("business_unit_id", String(businessUnitId));
            params.set("page", String(pageNumber));
            params.set("page_size", String(pageSizeForGrid));
            params.set("show_cancelled", "0");

            const reservationsRes = await axios.get(
                `/api/reservations?${params.toString()}`,
                { signal: controller.signal }
            );

            return reservationsRes?.data;
        };

        const fetchAllGridReservations = async () => {
            const firstPayload = await fetchReservationsPage(1);
            const firstRows = extractReservationRows(firstPayload);
            const totalPages = extractReservationTotalPages(firstPayload);

            if (totalPages <= 1) {
                return {
                    rows: firstRows,
                    pages: totalPages,
                };
            }

            const pageNumbers = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
            const rowsByPage = { 1: firstRows };
            let cursor = 0;
            const workerCount = Math.min(reservationPageConcurrency, pageNumbers.length);

            const worker = async () => {
                while (isActive) {
                    const index = cursor;
                    cursor += 1;
                    if (index >= pageNumbers.length) return;

                    const pageNumber = pageNumbers[index];
                    const payload = await fetchReservationsPage(pageNumber);
                    rowsByPage[pageNumber] = extractReservationRows(payload);
                }
            };

            if (workerCount > 0) {
                await Promise.all(Array.from({ length: workerCount }, () => worker()));
            }

            const allRows = [];
            for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
                const pageRows = rowsByPage[pageNumber];
                if (!Array.isArray(pageRows) || pageRows.length === 0) continue;
                allRows.push(...pageRows);
            }

            return {
                rows: allRows,
                pages: totalPages,
            };
        };

        const fetchRoomsForGrid = async () => {
            const roomsRes = await axios.get(
                `/api/rooms?business_unit_id=${businessUnitId}`,
                { signal: controller.signal }
            );

            const roomsList = Array.isArray(roomsRes.data) ? roomsRes.data : [];
            const counts = roomsList.reduce((acc, room) => {
                const catId = room?.room_category_id;
                if (!catId) return acc;
                const key = String(catId);
                acc[key] = (acc[key] || 0) + 1;
                return acc;
            }, {});
            const categoryNames = roomsList.reduce((acc, room) => {
                const catId = room?.room_category_id;
                if (!catId) return acc;
                const key = String(catId);
                const name =
                    room?.RoomCategory?.name ||
                    room?.room_category?.name ||
                    room?.room_category_name ||
                    acc[key] ||
                    `Category ${key}`;
                acc[key] = name;
                return acc;
            }, {});
            const numbersByCategory = roomsList.reduce((acc, room) => {
                const catId = room?.room_category_id;
                if (!catId) return acc;

                const key = String(catId);
                const roomId = room?.id ?? room?.room_id ?? null;
                const roomNumberRaw = room?.room_number ?? room?.number ?? room?.name ?? null;
                const roomNumber = roomNumberRaw != null ? String(roomNumberRaw).trim() : "";

                if (!acc[key]) acc[key] = [];
                if (roomId == null && !roomNumber) return acc;

                acc[key].push({
                    room_id: roomId != null ? String(roomId) : null,
                    room_number: roomNumber || `Room ${acc[key].length + 1}`,
                });
                return acc;
            }, {});

            Object.keys(numbersByCategory).forEach((categoryKey) => {
                const seen = new Set();
                const deduped = [];

                (numbersByCategory[categoryKey] || []).forEach((roomInfo) => {
                    const dedupeKey = roomInfo?.room_id
                        ? `id:${roomInfo.room_id}`
                        : `num:${String(roomInfo?.room_number || "").toLowerCase()}`;
                    if (seen.has(dedupeKey)) return;
                    seen.add(dedupeKey);
                    deduped.push(roomInfo);
                });

                deduped.sort((a, b) =>
                    String(a?.room_number || "").localeCompare(String(b?.room_number || ""), undefined, {
                        numeric: true,
                        sensitivity: "base",
                    })
                );
                numbersByCategory[categoryKey] = deduped;
            });

            const roomCatalog = roomsList.reduce((acc, room) => {
                const roomId = room?.id ?? room?.room_id ?? null;
                if (roomId == null) return acc;

                acc[String(roomId)] = {
                    ...room,
                    id: roomId,
                    room_number:
                        room?.room_number ??
                        room?.number ??
                        room?.name ??
                        `Room ${roomId}`,
                };
                return acc;
            }, {});

            return {
                roomsList,
                counts,
                categoryNames,
                numbersByCategory,
                roomCatalog,
            };
        };

        const fetchRoomBlocksForGrid = async () => {
            const blocksRes = await axios.get(
                `/api/room-blocks?business_unit_id=${businessUnitId}`,
                { signal: controller.signal }
            );

            return Array.isArray(blocksRes.data) ? blocksRes.data : [];
        };

        const fetchMissingGroupBookersInBackground = async (missingGroupIds) => {
            if (!isActive || !Array.isArray(missingGroupIds) || missingGroupIds.length === 0) return;

            const fetchedBookerMap = {};
            let cursor = 0;
            const workerCount = Math.min(reservationGroupFetchConcurrency, missingGroupIds.length);

            const worker = async () => {
                while (isActive) {
                    const index = cursor;
                    cursor += 1;
                    if (index >= missingGroupIds.length) return;

                    const groupId = missingGroupIds[index];
                    try {
                        const response = await axios.get(`/api/reservation-groups/${groupId}/full`, {
                            signal: controller.signal,
                        });
                        const group = response?.data?.group || response?.data || {};
                        const bookerName = getBookerDisplayName(group);
                        if (bookerName) fetchedBookerMap[groupId] = bookerName;
                    } catch (err) {
                        if (isCanceledError(err)) return;
                    }
                }
            };

            if (workerCount > 0) {
                await Promise.all(Array.from({ length: workerCount }, () => worker()));
            }

            if (!isActive || Object.keys(fetchedBookerMap).length === 0) return;

            setGroupBookerNamesByGroupId((prev) => ({
                ...(prev || {}),
                ...fetchedBookerMap,
            }));

            profile.mark("api.reservation_groups_background", {
                requested: missingGroupIds.length,
                resolved: Object.keys(fetchedBookerMap).length,
            });
        };

        (async () => {
            let hasError = false;
            try {
                const reservationsPromise = fetchAllGridReservations();
                const roomsPromise = fetchRoomsForGrid();
                const roomBlocksPromise = fetchRoomBlocksForGrid();

                try {
                    const reservationsResult = await reservationsPromise;
                    if (!isActive) return;

                    const allGridReservations = Array.isArray(reservationsResult?.rows)
                        ? reservationsResult.rows
                        : [];
                    const pages = Number(reservationsResult?.pages || 1);

                    setGridSourceReservations(allGridReservations);
                    console.log("[RES-LIST][Grid][Reservations] loaded", {
                        rows: allGridReservations.length,
                        pages,
                    });
                    profile.mark("api.reservations_all_pages", {
                        rows: allGridReservations.length,
                        pages,
                    });

                    const toCustomerName = (guest) => {
                        const customer = guest?.customer || {};
                        const full = `${customer?.first_name || ""} ${customer?.last_name || ""}`.trim();
                        if (full) return full;
                        return String(customer?.full_name || customer?.name || "").trim();
                    };

                    const rowsBookerMap = {};
                    const uniqueGroupIds = [];
                    const seenGroupIds = new Set();
                    (allGridReservations || []).forEach((reservation) => {
                        const groupIdRaw =
                            reservation?.reservation_group_id ??
                            reservation?.reservation_group?.id ??
                            reservation?.group_id ??
                            null;
                        if (groupIdRaw == null) return;
                        const groupId = String(groupIdRaw);
                        if (!seenGroupIds.has(groupId)) {
                            seenGroupIds.add(groupId);
                            uniqueGroupIds.push(groupId);
                        }

                        const guests = Array.isArray(reservation?.guests) ? reservation.guests : [];
                        const rowBooker = guests.find((guest) => guest?.is_booker);
                        const rowBookerName = getBookerDisplayName(reservation) || toCustomerName(rowBooker);
                        if (rowBookerName && !rowsBookerMap[groupId]) rowsBookerMap[groupId] = rowBookerName;
                    });

                    setGroupBookerNamesByGroupId(rowsBookerMap);
                    const missingGroupIds = uniqueGroupIds.filter((groupId) => !rowsBookerMap[groupId]);
                    profile.mark("compute.group_bookers_local", {
                        groups: uniqueGroupIds.length,
                        resolved: Object.keys(rowsBookerMap).length,
                        missing: missingGroupIds.length,
                    });

                    // Resolve missing group bookers in background so grid opening is not blocked.
                    void fetchMissingGroupBookersInBackground(missingGroupIds);
                } catch (err) {
                    if (isCanceledError(err)) return;
                    hasError = true;
                    console.error("[RES-LIST][Grid][Reservations][ERR] fetching reservations", err);
                    if (isActive) setGridSourceReservations([]);
                    if (isActive) setGroupBookerNamesByGroupId({});
                }

                const [roomsResult, roomBlocksResult] = await Promise.allSettled([
                    roomsPromise,
                    roomBlocksPromise,
                ]);

                if (roomsResult.status === "fulfilled") {
                    if (!isActive) return;
                    const { roomsList, counts, categoryNames, numbersByCategory, roomCatalog } = roomsResult.value || {};
                    console.log("[RES-LIST][Grid][RoomCountsByCategory] ready", counts);
                    setRoomCountsByCategory(counts || {});
                    setRoomCategoryNamesById(categoryNames || {});
                    setRoomNumbersByCategory(numbersByCategory || {});
                    setRoomCatalogById(roomCatalog || {});
                    profile.mark("api.rooms", { room_rows: Array.isArray(roomsList) ? roomsList.length : 0 });
                } else {
                    const err = roomsResult.reason;
                    if (!isCanceledError(err)) {
                        hasError = true;
                        console.error("[RES-LIST][Grid][RoomCountsByCategory][ERR] fetching rooms", err);
                        if (isActive) setRoomCountsByCategory({});
                        if (isActive) setRoomCategoryNamesById({});
                        if (isActive) setRoomNumbersByCategory({});
                        if (isActive) setRoomCatalogById({});
                    } else {
                        return;
                    }
                }

                if (roomBlocksResult.status === "fulfilled") {
                    if (!isActive) return;
                    const blocksList = Array.isArray(roomBlocksResult.value) ? roomBlocksResult.value : [];
                    console.log("[RES-LIST][Grid][RoomBlocks] loaded", { count: blocksList.length });
                    setRoomBlocks(blocksList);
                    profile.mark("api.room_blocks", { blocks_rows: blocksList.length });
                } else {
                    const err = roomBlocksResult.reason;
                    if (!isCanceledError(err)) {
                        hasError = true;
                        console.error("[RES-LIST][Grid][RoomBlocks][ERR] fetching room blocks", err);
                        if (isActive) setRoomBlocks([]);
                    } else {
                        return;
                    }
                }

                profile.end({ status: hasError ? "partial_error" : "ok" });
            } finally {
                if (isActive) setIsLoadingGridData(false);
            }
        })();

        return () => {
            isActive = false;
            controller.abort();
        };
    }, [businessUnitId, viewMode, crossTabRefreshToken]);



    


    // Filters: global search + column filters (popover)
    const formatCreatedAt = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat(language.locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).format(d);
    };

   
    

    const getColumnTitle = useCallback((key) => {
        if (key === "locator") return reservationsCopy.locator;
        if (key === "externalLocator") return reservationsCopy.externalLocator;
        if (key === "customer") return reservationsCopy.customer;
        if (key === "roomCategory") return reservationsCopy.roomCategory;
        if (key === "room") return reservationsCopy.room;
        if (key === "rate") return reservationsCopy.rate;
        if (key === "checkIn") return reservationsCopy.checkIn;
        if (key === "checkOut") return reservationsCopy.checkOut;
        if (key === "roomNights") return reservationsCopy.roomNights;
        if (key === "adults") return reservationsCopy.adults;
        if (key === "children") return reservationsCopy.children;
        if (key === "babies") return reservationsCopy.babies;
        if (key === "status") return reservationsCopy.reservationStatus;
        if (key === "totalPrice") return reservationsCopy.totalPrice;
        if (key === "externalAgency") return reservationsCopy.externalAgency;
        if (key === "createdAt") return reservationsCopy.createdAt;
        return String(key || "");
    }, [reservationsCopy]);

    const translateStatus = useCallback(
        (status) => translateReservationStatus(status, languageId) || status || commonCopy.notAvailable,
        [commonCopy.notAvailable, languageId]
    );

    const columnIsFiltered = (key) => {
        const value = columnFilters[key];
        return Array.isArray(value) ? value.length > 0 : String(value || "").trim().length > 0;
    };

    const activeFilterCount = useMemo(
        () => RES_LIST_FILTER_KEYS.reduce((count, key) => {
            const value = columnFilters[key];
            const isActive = Array.isArray(value)
                ? value.length > 0
                : String(value || "").trim().length > 0;
            return count + (isActive ? 1 : 0);
        }, 0),
        [columnFilters]
    );

    const clearAllReservationFilters = () => {
        setColumnFilters(getDefaultReservationColumnFilters());
        setActiveFilter(null);
    };

    const toggleMultiSelectFilter = (key, value) => {
        setColumnFilters((prev) => {
            const prevArr = Array.isArray(prev[key]) ? prev[key] : [];
            const exists = prevArr.includes(value);
            const nextArr = exists ? prevArr.filter((v) => v !== value) : [...prevArr, value];
            return { ...prev, [key]: nextArr };
        });
    };

    const clearColumnFilter = (key) => {
        setColumnFilters((prev) => ({
            ...prev,
            [key]: Array.isArray(prev[key]) ? [] : "",
        }));
    };

    const closeFilterPopover = () => setActiveFilter(null);

    const toggleFilterPopover = (key, el) => {
        setActiveFilter((prev) => {
            if (prev?.key === key) return null;
            return { key, rect: el.getBoundingClientRect() };
        });
    };

    useEffect(() => {
        if (viewMode === "grid") closeFilterPopover();
    }, [viewMode]);

    useEffect(() => {
        setFilterOptionQuery("");
    }, [activeFilter?.key]);

    useEffect(() => {
        if (!activeFilter?.key) return undefined;

        const onKeyDown = (e) => {
            if (e.key === "Escape") closeFilterPopover();
        };

        const onMouseDown = (e) => {
            if (filterPopoverRef.current?.contains(e.target)) return;
            if (e.target?.closest?.("[data-res-filter-trigger]")) return;
            closeFilterPopover();
        };

        const onResize = () => closeFilterPopover();

        const onScroll = (e) => {
            if (filterPopoverRef.current?.contains(e.target)) return;
            closeFilterPopover();
        };

        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("resize", onResize);
        window.addEventListener("scroll", onScroll, true);

        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("mousedown", onMouseDown);
            window.removeEventListener("resize", onResize);
            window.removeEventListener("scroll", onScroll, true);
        };
    }, [activeFilter?.key]);

    const setFilterStats = useMemo(() => {
        if (serverFilterOptions && typeof serverFilterOptions === "object") {
            const parseOptions = (key) => {
                const rawOptions = Array.isArray(serverFilterOptions?.[key]) ? serverFilterOptions[key] : [];
                return rawOptions.map((opt) => ({
                    value: opt?.value,
                    label:
                        key === "status"
                            ? translateStatus(opt?.label ?? opt?.value ?? "")
                            : String(opt?.label ?? opt?.value ?? "\u2014"),
                    count: Number(opt?.count || 0),
                }));
            };

            const withLabels = (options) => ({
                options,
                labels: new Map(options.map((opt) => [String(opt.value), String(opt.label)])),
            });

            return {
                roomCategory: withLabels(parseOptions("roomCategory")),
                room: withLabels(parseOptions("room")),
                rate: withLabels(parseOptions("rate")),
                checkIn: withLabels(parseOptions("checkIn")),
                checkOut: withLabels(parseOptions("checkOut")),
                status: withLabels(parseOptions("status")),
                externalAgency: withLabels(parseOptions("externalAgency")),
                roomNights: { options: parseOptions("roomNights") },
                adults: { options: parseOptions("adults") },
                children: { options: parseOptions("children") },
                babies: { options: parseOptions("babies") },
            };
        }

        const buildStringStats = (getLabel) => {
            const counts = new Map();
            const labels = new Map(); // key -> label (first seen)

            (visibleReservations || []).forEach((r) => {
                const raw = String(getLabel(r) ?? "").trim();
                const label = raw || "\u2014";
                const key = label.toLowerCase();
                counts.set(key, (counts.get(key) || 0) + 1);
                if (!labels.has(key)) labels.set(key, label);
            });

            const options = Array.from(labels.entries())
                .map(([key, label]) => ({
                    value: key,
                    label,
                    count: counts.get(key) || 0,
                }))
                .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

            return { options, counts, labels };
        };

        const buildNumberStats = (getNumber) => {
            const counts = new Map();
            (visibleReservations || []).forEach((r) => {
                const n = Number(getNumber(r));
                if (!Number.isFinite(n)) return;
                counts.set(n, (counts.get(n) || 0) + 1);
            });

            const options = Array.from(counts.keys())
                .sort((a, b) => a - b)
                .map((n) => ({ value: n, label: String(n), count: counts.get(n) || 0 }));

            return { options, counts };
        };

        return {
            roomCategory: buildStringStats((r) => r?.room_category?.name || commonCopy.notAvailable),
            room: buildStringStats((r) => r?.room?.room_number || "—"),
            rate: buildStringStats((r) => {
                const rateName = r?.rate?.name || commonCopy.notAvailable;
                return r?.group_rate_id ? `${reservationsCopy.groupRatePrefix}: ${rateName}` : rateName;
            }),
            checkIn: buildStringStats((r) => r?.check_in_date || "\u2014"),
            checkOut: buildStringStats((r) => r?.check_out_date || "\u2014"),
            status: buildStringStats((r) => translateStatus(String(r?.status || "").trim() || "\u2014")),
            externalAgency: buildStringStats((r) => r?.external_agency_name || "\u2014"),
            roomNights: buildNumberStats((r) => r?.room_nights),
            adults: buildNumberStats((r) => r?.adults),
            children: buildNumberStats((r) => r?.children),
            babies: buildNumberStats((r) => r?.babies),
        };
    }, [commonCopy.notAvailable, reservationsCopy.groupRatePrefix, serverFilterOptions, translateStatus, visibleReservations]);

    const columnSubtitle = (key) => {
        const value = columnFilters[key];

        if (Array.isArray(value)) {
            if (!value.length) return reservationsCopy.all;

            if (value.length === 1) {
                const only = value[0];
                const map =
                    key === "roomCategory"
                        ? setFilterStats.roomCategory.labels
                        : key === "room"
                            ? setFilterStats.room.labels
                            : key === "rate"
                                ? setFilterStats.rate.labels
                                : key === "checkIn"
                                    ? setFilterStats.checkIn.labels
                                    : key === "checkOut"
                                        ? setFilterStats.checkOut.labels
                                        : key === "status"
                                            ? setFilterStats.status.labels
                                            : key === "externalAgency"
                                                ? setFilterStats.externalAgency.labels
                                                : null;

                if (map && typeof only === "string") return map.get(only) ?? only;
                return String(only);
            }

            if (key === "roomCategory") return reservationsCopy.categoriesCount(value.length);
            if (key === "room") return reservationsCopy.roomsCount(value.length);
            if (key === "rate") return reservationsCopy.ratesCount(value.length);
            if (key === "checkIn") return reservationsCopy.datesCount(value.length);
            if (key === "checkOut") return reservationsCopy.datesCount(value.length);
            if (key === "status") return reservationsCopy.statusesCount(value.length);
            if (key === "externalAgency") return reservationsCopy.agenciesCount(value.length);
            return reservationsCopy.selectedCount(value.length);
        }

        const needle = String(value || "").trim();
        if (!needle) return reservationsCopy.all;
        return reservationsCopy.contains(needle);
    };

    const filterModel = useMemo(() => {
        const key = activeFilter?.key;
        if (!key) return null;

        if (key === "locator") return { key, title: getColumnTitle(key), type: "text" };
        if (key === "externalLocator") return { key, title: getColumnTitle(key), type: "text" };
        if (key === "customer") return { key, title: getColumnTitle(key), type: "text" };
        if (key === "totalPrice") return { key, title: getColumnTitle(key), type: "text" };
        if (key === "createdAt") return { key, title: getColumnTitle(key), type: "text" };

        if (key === "roomCategory")
            return { key, title: getColumnTitle(key), type: "set", options: setFilterStats.roomCategory.options };
        if (key === "room") return { key, title: getColumnTitle(key), type: "set", options: setFilterStats.room.options };
        if (key === "rate") return { key, title: getColumnTitle(key), type: "set", options: setFilterStats.rate.options };
        if (key === "checkIn") return { key, title: getColumnTitle(key), type: "set", options: setFilterStats.checkIn.options };
        if (key === "checkOut") return { key, title: getColumnTitle(key), type: "set", options: setFilterStats.checkOut.options };
        if (key === "roomNights")
            return { key, title: getColumnTitle(key), type: "set", options: setFilterStats.roomNights.options };
        if (key === "adults") return { key, title: getColumnTitle(key), type: "set", options: setFilterStats.adults.options };
        if (key === "children") return { key, title: getColumnTitle(key), type: "set", options: setFilterStats.children.options };
        if (key === "babies") return { key, title: getColumnTitle(key), type: "set", options: setFilterStats.babies.options };
        if (key === "status")
            return { key, title: getColumnTitle(key), type: "set", options: setFilterStats.status.options };
        if (key === "externalAgency")
            return { key, title: getColumnTitle(key), type: "set", options: setFilterStats.externalAgency.options };

        return null;
    }, [activeFilter?.key, getColumnTitle, setFilterStats]);

    const filterPopoverPos = useMemo(() => {
        if (!activeFilter?.rect) return null;

        const rect = activeFilter.rect;
        const pad = 12;
        const viewportW = window.innerWidth || 1200;
        const viewportH = window.innerHeight || 800;

        const width = Math.min(380, viewportW - pad * 2);
        const estimatedHeight = 420;

        let left = rect.left;
        left = Math.max(pad, Math.min(left, viewportW - width - pad));

        const showBelow = rect.bottom + 10 + estimatedHeight <= viewportH - pad;
        let top = showBelow ? rect.bottom + 10 : rect.top - estimatedHeight - 10;
        top = Math.max(pad, Math.min(top, viewportH - estimatedHeight - pad));

        return { top, left, width };
    }, [activeFilter?.rect]);

    const visibleFilterOptions = useMemo(() => {
        if (!filterModel || filterModel.type !== "set") return [];
        const needle = filterOptionQuery.trim().toLowerCase();
        if (!needle) return filterModel.options;

        return filterModel.options.filter((o) => {
            const label = String(o.label || "").toLowerCase();
            const meta = String(o.meta || "").toLowerCase();
            return label.includes(needle) || meta.includes(needle);
        });
    }, [filterModel, filterOptionQuery]);

    const selectAllVisibleOptions = () => {
        if (!filterModel || filterModel.type !== "set") return;
        setColumnFilters((prev) => ({
            ...prev,
            [filterModel.key]: visibleFilterOptions.map((o) => o.value),
        }));
    };

    const filteredReservations = useMemo(() => visibleReservations || [], [visibleReservations]);

    const totalPages = useMemo(
        () => ((Number(totalRows) > 0) ? Math.ceil(Number(totalRows) / pageSize) : 1),
        [totalRows, pageSize]
    );
    const pageStart = totalRows > 0 ? ((page - 1) * pageSize) + 1 : 0;
    const pageEnd = totalRows > 0 ? Math.min(Number(totalRows), ((page - 1) * pageSize) + filteredReservations.length) : 0;

    const exportToExcel = () => {
        // Verifica que haya datos visibles para exportar
        if (filteredReservations.length === 0) {
            alert(reservationsCopy.noDataToExport);
            return;
        }

        const totalPriceExportLabel = `${reservationsCopy.totalPrice} (${displayCurrency})`;
        const paidAmountExportLabel = `${reservationsCopy.paidAmount} (${displayCurrency})`;
    
        // Mapeamos los datos visibles
        const exportData = filteredReservations.map((reservation) => {
            const extrasTotal = Number(reservation.reservation_extras_total ?? reservation.extras_total ?? 0);
            const roomTotal = Number(reservation.total_price ?? 0);
            const finalTotal = isNaN(roomTotal + extrasTotal) ? 0 : roomTotal + extrasTotal;

            const paid = Number(paidMap[reservation.id] ?? reservation.paid_amount ?? 0);

            return ({
                [reservationsCopy.locator]: getReservationDisplayLocator(reservation),
                [reservationsCopy.customer]: getReservationCustomerName(reservation) || commonCopy.notAvailable,
                [reservationsCopy.booker]: getBookerDisplayName(reservation),
                "Booker contact": getBookerContactName(reservation),
                [reservationsCopy.roomCategory]: reservation.room_category
                    ? reservation.room_category.name
                    : commonCopy.notAvailable,
                [reservationsCopy.room]: reservation.room ? reservation.room.room_number : commonCopy.unassigned,
                [reservationsCopy.rate]: reservation.rate ? reservation.rate.name : commonCopy.notAvailable,
                [reservationsCopy.checkIn]: reservation.check_in_date,
                [reservationsCopy.checkOut]: reservation.check_out_date,
                [reservationsCopy.roomNights]: reservation.room_nights,
                [reservationsCopy.adults]: reservation.adults,
                [reservationsCopy.children]: reservation.children,
                [reservationsCopy.babies]: reservation.babies,
                [reservationsCopy.reservationStatus]: translateStatus(reservation.status),
                [totalPriceExportLabel]: convertFromBase(finalTotal),
                ...(permissions["reservations.view_payments"]
                    ? { [paidAmountExportLabel]: isNaN(paid) ? 0 : convertFromBase(paid) }
                    : {}),
                [reservationsCopy.createdBy]: reservation.created_by || commonCopy.notAvailable
            });
        });

    
        // Creamos la hoja de Excel
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, reservationsCopy.workbookSheetName);
    
        // Generamos y descargamos el archivo
        XLSX.writeFile(workbook, reservationsCopy.workbookFileName);
    };

    const fetchAvailableRoomsForReservation = useCallback(
        async (reservation) => {
            if (!businessUnitId || !reservation) return [];

            const roomCategoryId =
                reservation?.room_category_id ??
                reservation?.room_category?.id ??
                reservation?.room?.room_category_id ??
                null;

            if (!roomCategoryId || !reservation?.check_in_date || !reservation?.check_out_date) return [];

            const response = await axios.get(`/api/rooms/available`, {
                params: {
                    business_unit_id: businessUnitId,
                    room_category_id: roomCategoryId,
                    check_in_date: reservation.check_in_date,
                    check_out_date: reservation.check_out_date,
                    exclude_reservation_id: reservation?.id ?? undefined,
                },
            });

            const rows = Array.isArray(response?.data) ? response.data : [];
            return rows.sort((a, b) =>
                String(a?.room_number || "").localeCompare(String(b?.room_number || ""), undefined, {
                    numeric: true,
                    sensitivity: "base",
                })
            );
        },
        [businessUnitId]
    );

    const assignRoomToReservation = useCallback(
        async (reservation, targetRoomId, options = {}) => {
            const reservationId = reservation?.id;
            if (!reservationId) {
                throw new Error("Missing reservation.");
            }

            const isUnassignOperation =
                targetRoomId == null ||
                String(targetRoomId).trim() === "" ||
                Number(targetRoomId) <= 0;

            const numericRoomId = isUnassignOperation ? null : Number(targetRoomId);
            if (!isUnassignOperation && (!Number.isFinite(numericRoomId) || numericRoomId <= 0)) {
                throw new Error("Invalid room selected.");
            }
            const isCheckedInReservation =
                String(reservation?.status || "").trim().toLowerCase() === "checked in";
            if (isUnassignOperation && isCheckedInReservation) {
                throw new Error("A checked-in reservation cannot be left without a room.");
            }
            const effectiveStayDate =
                options?.effectiveStayDate ||
                options?.effective_stay_date ||
                options?.dateIso ||
                null;

            console.log("[RES-LIST][AssignRoom] start", {
                reservationId,
                targetRoomId,
                numericRoomId,
                isUnassignOperation,
                effectiveStayDate,
                reservation,
            });

            try {
                const payload = {
                    room_id: isUnassignOperation ? null : numericRoomId,
                    updated_by: "GridRoomMove",
                    ...(isCheckedInReservation && effectiveStayDate
                        ? { effective_stay_date: effectiveStayDate }
                        : {}),
                };
                console.log("[RES-LIST][AssignRoom] PATCH /api/reservations/:id/room-assignment", {
                    reservationId,
                    payload,
                });
                const response = await axios.patch(`/api/reservations/${reservationId}/room-assignment`, payload);
                console.log("[RES-LIST][AssignRoom] PATCH success", {
                    reservationId,
                    room_id: isUnassignOperation ? null : numericRoomId,
                    response: response?.data,
                });
                const freshReservation = response?.data?.reservation || null;
                const roomMeta = !isUnassignOperation ? roomCatalogById?.[String(numericRoomId)] || null : null;
                const fallbackReservation = {
                    ...reservation,
                    room_id: isUnassignOperation ? null : numericRoomId,
                    effective_room_id: isUnassignOperation ? null : numericRoomId,
                    effective_room_number:
                        isUnassignOperation
                            ? null
                            : roomMeta?.room_number ?? reservation?.effective_room_number ?? null,
                    room: isUnassignOperation
                        ? null
                        : roomMeta
                            ? {
                                id: roomMeta.id,
                                room_number: roomMeta.room_number,
                                room_category_id:
                                    roomMeta.room_category_id ??
                                    reservation?.room_category_id ??
                                    reservation?.room_category?.id ??
                                    null,
                            }
                            : reservation?.room,
                    effective_room: isUnassignOperation
                        ? null
                        : roomMeta
                            ? {
                                id: roomMeta.id,
                                room_number: roomMeta.room_number,
                                room_category_id:
                                    roomMeta.room_category_id ??
                                    reservation?.room_category_id ??
                                    reservation?.room_category?.id ??
                                    null,
                            }
                            : reservation?.effective_room ?? reservation?.room,
                };

                const appliedReservation = freshReservation || fallbackReservation;
                console.log("[RES-LIST][AssignRoom] apply result", {
                    reservationId,
                    appliedReservation,
                    usedFallback: !freshReservation,
                });

                setGridSourceReservations((prev) =>
                    (prev || []).map((item) =>
                        item?.id === reservationId ? { ...item, ...appliedReservation } : item
                    )
                );
                setReservations((prev) =>
                    (prev || []).map((item) =>
                        item?.id === reservationId ? { ...item, ...appliedReservation } : item
                    )
                );

                return appliedReservation;
            } catch (error) {
                console.warn("[RES-LIST][AssignRoom] PATCH error", {
                    reservationId,
                    room_id: isUnassignOperation ? null : numericRoomId,
                    status: error?.response?.status,
                    data: error?.response?.data,
                    message: error?.message,
                });
                const backendMessage =
                    error?.response?.data?.message ||
                    error?.response?.data?.details ||
                    error?.response?.data?.error ||
                    error?.response?.data?.detail ||
                    error?.message ||
                    "Could not assign this room.";
                throw new Error(String(backendMessage));
            }
        },
        [roomCatalogById]
    );
    

    return (
        <div className="reservations-container rm-page">
            {canRenderCurrentView && (
            viewMode === "grid" ? (
                <OccupancyGrid
                    readOnly={demoReadOnlyActive}
                    referenceDate={demoReadOnlyActive ? demoReferenceDate() : undefined}
                    reservations={gridReservations}
                    roomBlocks={roomBlocks}
                    isLoading={isLoadingGridData}
                    businessUnitId={businessUnitId}
                    roomCountsByCategory={roomCountsByCategory}
                    roomCategoryNamesById={roomCategoryNamesById}
                    roomNumbersByCategory={roomNumbersByCategory}
                    groupBookerNamesByGroupId={groupBookerNamesByGroupId}
                    onFetchAvailableRooms={demoReadOnlyActive ? undefined : fetchAvailableRoomsForReservation}
                    onAssignRoom={demoReadOnlyActive ? undefined : assignRoomToReservation}
                    daysPerPage={9}
                    initialGridViewMode={requestedGridViewMode === "room_numbers" ? "room_numbers" : "lanes"}
                    initialStartDate={requestedGridStartDate}
                    highlightReservationId={highlightedReservationId}
                    onBackToList={() => {
                        console.log("[RES-LIST][ViewMode][BackToList]", { from: "grid", to: "list" });
                        setViewMode("list");
                    }}
                    onOpenReservation={(reservation) => {
                        if (reservation?.__type === "room_block") {
                            if (demoReadOnlyActive) return;
                            console.log("[RES-LIST][Grid][Open][ROOM_BLOCK] redirecting to Rooms Management (Room Blocks tab)", {
                                businessUnitId,
                                block_id: reservation?.block_id,
                                room_id: reservation?.room_id,
                            });

                            navigate(`/rooms-management?business_unit_id=${businessUnitId}&tab=blocks`);
                            return;
                        }


                        if (!canViewReservationDetails) {
                            alert(reservationsCopy.detailsPermissionAlert);
                            return;
                        }

                        navigate(
                            `/reservation-group/${reservation.reservation_group_id}?mode=view&business_unit_id=${businessUnitId}`
                        );
                    }}
                />




            ) : (
                <div className="rm-rates-card">
                    <div className="rm-rates-header">
                        <div className="res-pageHeadingRow">
                            <div className="res-pageHeadingCopy">
                                <h3 className="rm-rates-title">{reservationsCopy.pageTitle}</h3>
                                <div className="rm-rates-subtitle">
                                    {reservationsCopy.loadedCount(Number(totalRows) || 0)}
                                </div>
                            </div>

                            {permissions["reservations.create"] && (
                                <button
                                    type="button"
                                    className="rm-topBtn rm-topBtn--primary"
                                    onClick={async () => {
                                        console.log("[RES-LIST][Add] creating group & first reservation", { businessUnitId });
                                        try {
                                            const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/reservation-drafts/create`, {
                                                number_of_rooms: 1,
                                                created_by: "Admin",
                                                business_unit_id: Number(businessUnitId),
                                            });

                                            const reservationGroupId = res.data.reservation_group_id;
                                            console.log("[RES-LIST][Add][OK][GROUP]", { reservationGroupId });
                                            navigate(`/reservation-group/${reservationGroupId}`);
                                        } catch (error) {
                                            console.error("[RES-LIST][Add][ERR]", error);
                                            alert(reservationsCopy.errorCreatingGroup);
                                        }
                                    }}
                                >
                                    <FiPlus size={17} aria-hidden="true" />
                                    <span>{reservationsCopy.add}</span>
                                </button>
                            )}
                        </div>

                        {permissions["reservations.view_list"] && (
                            <div className="res-toolbarRow">
                                <div className="res-searchField">
                                    <FiSearch size={17} aria-hidden="true" />
                                    <input
                                        type="search"
                                        className="rm-searchInput"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder={reservationsCopy.searchPlaceholder}
                                        aria-label={reservationsCopy.searchPlaceholder}
                                    />
                                </div>

                                <div className="rm-topActions">
                                    <button
                                        type="button"
                                        className={`rm-topBtn rm-topBtn--cancelled${showCancelled ? " is-active" : ""}`}
                                        aria-pressed={showCancelled}
                                        onClick={() => {
                                            const next = !showCancelled;
                                            console.log("[RES-LIST][Cancelled][Toggle]", { from: showCancelled, to: next });
                                            setShowCancelled(next);
                                        }}
                                    >
                                        {showCancelled ? (
                                            <FiEyeOff size={16} aria-hidden="true" />
                                        ) : (
                                            <FiEye size={16} aria-hidden="true" />
                                        )}
                                        <span>
                                            {showCancelled ? reservationsCopy.hideCancelled : reservationsCopy.viewCancelled}
                                        </span>
                                    </button>

                                    <button
                                        type="button"
                                        className={`rm-topBtn rm-topBtn--filters${filtersExpanded ? " is-active" : ""}`}
                                        aria-expanded={filtersExpanded}
                                        onClick={() => {
                                            setFiltersExpanded((previous) => {
                                                const next = !previous;
                                                if (!next) closeFilterPopover();
                                                return next;
                                            });
                                        }}
                                    >
                                        <FiFilter size={16} aria-hidden="true" />
                                        <span>{reservationsCopy.filters}</span>
                                        {activeFilterCount > 0 ? (
                                            <span className="res-activeFilterCount" aria-label={`${activeFilterCount}`}>
                                                {activeFilterCount}
                                            </span>
                                        ) : null}
                                    </button>

                                    <button
                                        type="button"
                                        className="rm-topBtn rm-topBtn--view"
                                        onClick={() => {
                                            console.log("[RES-LIST][ViewMode][Toggle]", { from: viewMode, to: "grid" });
                                            setViewMode("grid");
                                        }}
                                    >
                                        <FiGrid size={16} aria-hidden="true" />
                                        <span>{reservationsCopy.occupancyGrid}</span>
                                    </button>

                                    <button
                                        type="button"
                                        className="rm-topBtn rm-topBtn--tertiary"
                                        onClick={exportToExcel}
                                    >
                                        <FiDownload size={16} aria-hidden="true" />
                                        <span>{reservationsCopy.exportToExcel}</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {permissions["reservations.view_list"] && filtersExpanded ? (
                            <div className="res-filterTray" aria-label={reservationsCopy.filters}>
                                <div className="res-filterTrayHeading">
                                    <div>
                                        <strong>{reservationsCopy.filters}</strong>
                                        <span>{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ""}</span>
                                    </div>
                                    {activeFilterCount > 0 ? (
                                        <button
                                            type="button"
                                            className="res-filterTrayClear"
                                            onClick={clearAllReservationFilters}
                                        >
                                            {reservationsCopy.clearAllFilters}
                                        </button>
                                    ) : null}
                                </div>

                                <div className="res-filterTrayGrid">
                                    {RES_LIST_FILTER_KEYS.map((key) => (
                                        <button
                                            key={key}
                                            type="button"
                                            data-res-filter-trigger={key}
                                            className={`res-filterTrigger${columnIsFiltered(key) ? " is-filtered" : ""}${
                                                activeFilter?.key === key ? " is-open" : ""
                                            }`}
                                            onClick={(event) => toggleFilterPopover(key, event.currentTarget)}
                                        >
                                            <span className="res-filterTriggerCopy">
                                                <span className="res-filterTriggerLabel">{getColumnTitle(key)}</span>
                                                <span className="res-filterTriggerValue">{columnSubtitle(key)}</span>
                                            </span>
                                            <FiFilter size={14} aria-hidden="true" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>
                    <div
                        className="res-listMetaRow rm-listMetaRow"
                    >
                        <small className="res-listMetaText">
                            {reservationsCopy.showingRange(pageStart, pageEnd, Number(totalRows) || 0)}
                        </small>

                        <div className="res-pager">
                            <label htmlFor="res-page-size" className="res-listMetaText">
                                {reservationsCopy.size}
                            </label>
                            <select
                                className="res-pageSizeSelect"
                                id="res-page-size"
                                value={pageSize}
                                onChange={(e) => {
                                    const nextSize = Number(e.target.value) || RES_LIST_DEFAULT_PAGE_SIZE;
                                    setPageSize(nextSize);
                                    setPage(1);
                                }}
                            >
                                {RES_LIST_PAGE_SIZE_OPTIONS.map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>

                            <button
                                type="button"
                                className="res-pagerBtn"
                                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                disabled={page <= 1}
                            >
                                {reservationsCopy.prev}
                            </button>

                            <small className="res-pageCounter">
                                {reservationsCopy.pageCounter(page, totalPages)}
                            </small>

                            <button
                                type="button"
                                className="res-pagerBtn"
                                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                disabled={page >= totalPages}
                            >
                                {reservationsCopy.next}
                            </button>
                        </div>
                    </div>
                    <div className="rm-rates-table-wrap">
                        <table className="rm-rates-table">
                    <colgroup>
                        <col className="res-col res-col--locator" />
                        <col className="res-col res-col--external-locator" />
                        <col className="res-col res-col--customer" />
                        <col className="res-col res-col--room-category" />
                        <col className="res-col res-col--room" />
                        <col className="res-col res-col--rate" />
                        <col className="res-col res-col--check-in" />
                        <col className="res-col res-col--check-out" />
                        <col className="res-col res-col--nights" />
                        <col className="res-col res-col--occupancy" />
                        <col className="res-col res-col--total" />
                        <col className="res-col res-col--status" />
                        <col className="res-col res-col--agency" />
                        <col className="res-col res-col--created" />
                    </colgroup>

                    <thead>
                        <tr>
                            <th className="res-fieldTh rm-sticky-1">{getColumnTitle("locator")}</th>
                            <th className="res-fieldTh">{getColumnTitle("externalLocator")}</th>
                            <th className="res-fieldTh">{getColumnTitle("customer")}</th>
                            <th className="res-fieldTh">{getColumnTitle("roomCategory")}</th>
                            <th className="res-fieldTh res-fieldTh--center">{getColumnTitle("room")}</th>
                            <th className="res-fieldTh">{getColumnTitle("rate")}</th>
                            <th className="res-fieldTh res-fieldTh--center">{getColumnTitle("checkIn")}</th>
                            <th className="res-fieldTh res-fieldTh--center">{getColumnTitle("checkOut")}</th>
                            <th className="res-fieldTh res-fieldTh--center">{getColumnTitle("roomNights")}</th>
                            <th className="res-fieldTh res-fieldTh--center">
                                {getColumnTitle("adults")} / {getColumnTitle("children")}
                            </th>
                            <th className="res-fieldTh res-fieldTh--right">{getColumnTitle("totalPrice")}</th>
                            <th className="res-fieldTh">{getColumnTitle("status")}</th>
                            <th className="res-fieldTh">{getColumnTitle("externalAgency")}</th>
                            <th className="res-fieldTh">{getColumnTitle("createdAt")}</th>
                        </tr>
                    </thead>

                    <tbody>
                        {isLoadingList ? (
                            <tr>
                                <td colSpan="14" className="res-tableStateCell">
                                    <div className="d-inline-flex align-items-center gap-2">
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                        <span>{reservationsCopy.loadingReservations}</span>
                                    </div>
                                </td>
                            </tr>
                        ) : filteredReservations.length > 0 ? (
                            filteredReservations.map((reservation) => {
                                const rowIsCancelled = isCancelledReservation(reservation);
                                const normalizedStatus = String(reservation?.status || "").trim().toLowerCase();
                                const statusChipClass = rowIsCancelled
                                    ? "rm-chip--off"
                                    : normalizedStatus === "confirmed"
                                        ? "rm-chip--ok"
                                        : ["checked_in", "checked-in", "in_house", "in-house", "active"].includes(normalizedStatus)
                                            ? "rm-chip--active"
                                            : "rm-chip--soft";

                                const displayLocator = getReservationDisplayLocator(reservation) || "—";
                                const externalLocator = [
                                    reservation?.external_reservation_id,
                                    reservation?.external_partner_locator,
                                ].filter(Boolean).join(" / ") || "—";

                                const primaryGuest = reservation?.guests?.[0] || null;
                                const customerName = getReservationCustomerName(reservation) || commonCopy.notAvailable;
                                const companyBookerName = isCompanyBooker(reservation) ? getBookerDisplayName(reservation) : "";

                                const categoryName = reservation?.room_category?.name || commonCopy.notAvailable;
                                const roomNumber = reservation?.room?.room_number || "—";
                                const rateName = reservation?.rate?.name || commonCopy.notAvailable;
                                const rateDisplay = reservation?.group_rate_id
                                    ? reservationsCopy.groupRatePrefix + ": " + rateName
                                    : rateName;
                                const channelName = reservation?.external_agency_name || "—";

                                const extrasTotal = Number(
                                    reservation?.reservation_extras_total ?? reservation?.extras_total ?? 0
                                );
                                const roomTotal = Number(reservation?.total_price ?? 0);
                                const calculatedTotal = roomTotal + extrasTotal;
                                const finalTotal = Number.isFinite(calculatedTotal) ? calculatedTotal : 0;

                                return (
                                    <tr
                                        key={reservation.id}
                                        className={rowIsCancelled ? "res-row--cancelled" : ""}
                                    >
                                        <td className="rm-sticky-1">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (!canViewReservationDetails) {
                                                        alert(reservationsCopy.detailsPermissionAlert);
                                                        return;
                                                    }

                                                    navigate(
                                                        "/reservation-group/" +
                                                            reservation.reservation_group_id +
                                                            "?mode=view&business_unit_id=" +
                                                            businessUnitId
                                                    );
                                                }}
                                                className="res-locatorBtn"
                                                disabled={!canViewReservationDetails}
                                                title={
                                                    canViewReservationDetails
                                                        ? reservationsCopy.openReservation + ": " + displayLocator
                                                        : reservationsCopy.detailsPermissionRequired
                                                }
                                            >
                                                {displayLocator}
                                            </button>
                                        </td>

                                        <td title={externalLocator}>{externalLocator}</td>

                                        <td title={customerName}>
                                            <span className="res-customerName">{customerName}</span>
                                            {primaryGuest?.is_booker || (companyBookerName && companyBookerName === customerName) ? (
                                                <span className="res-bookerMark">({reservationsCopy.booker})</span>
                                            ) : null}
                                            {companyBookerName && companyBookerName !== customerName ? (
                                                <div className="res-bookerMark">{reservationsCopy.booker}: {companyBookerName}</div>
                                            ) : null}
                                        </td>

                                        <td title={categoryName}>{categoryName}</td>
                                        <td className="res-cell--center" title={String(roomNumber)}>{roomNumber}</td>
                                        <td
                                            className={reservation?.group_rate_id ? "res-rateCell--group" : ""}
                                            title={rateDisplay}
                                        >
                                            {rateDisplay}
                                        </td>
                                        <td className="res-cell--center res-dateCell">{reservation?.check_in_date || "—"}</td>
                                        <td className="res-cell--center res-dateCell">{reservation?.check_out_date || "—"}</td>
                                        <td className="res-cell--center res-numberCell">{reservation?.room_nights ?? 0}</td>
                                        <td
                                            className="res-cell--center res-numberCell res-occupancyCell"
                                            title={`${getColumnTitle("adults")}: ${reservation?.adults ?? 0} · ${getColumnTitle("children")}: ${reservation?.children ?? 0}`}
                                        >
                                            <span>{reservation?.adults ?? 0}</span>
                                            <span className="res-occupancySeparator" aria-hidden="true">/</span>
                                            <span>{reservation?.children ?? 0}</span>
                                        </td>
                                        <td className="res-totalCell">{formatFromBase(finalTotal)}</td>
                                        <td>
                                            <span className={"rm-chip " + statusChipClass}>
                                                {translateStatus(reservation?.status || commonCopy.notAvailable)}
                                            </span>
                                        </td>
                                        <td title={channelName}>{channelName}</td>
                                        <td className="res-createdCell">{formatCreatedAt(reservation?.createdAt)}</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="14" className="res-tableStateCell">
                                    {reservationsCopy.noReservationsFound}
                                </td>
                            </tr>
                        )}
                    </tbody>
                        </table>
                    </div>
                </div>
        )
        )}

        {permissions["reservations.view_list"] && viewMode !== "grid" && activeFilter?.key && filterModel && filterPopoverPos ? (
            <div
                ref={filterPopoverRef}
                className="res-filterPopover"
                role="dialog"
                aria-label={filterModel.title}
                style={{ top: filterPopoverPos.top, left: filterPopoverPos.left, width: filterPopoverPos.width }}
            >
                <div className="res-filterHeader">
                    <div className="res-filterTitle">{filterModel.title}</div>
                    <div className="res-filterHeaderActions">
                        {columnIsFiltered(filterModel.key) ? (
                            <button type="button" className="res-filterSmallBtn" onClick={() => clearColumnFilter(filterModel.key)}>
                                {commonCopy.clear}
                            </button>
                        ) : null}

                        {filterModel.type === "set" ? (
                            <button type="button" className="res-filterSmallBtn" onClick={selectAllVisibleOptions}>
                                {commonCopy.all}
                            </button>
                        ) : null}

                        <button
                            type="button"
                            className="res-filterCloseBtn"
                            onClick={closeFilterPopover}
                            aria-label="Close filter"
                        >
                            ×
                        </button>
                    </div>
                </div>

                <div className="res-filterBody">
                    {filterModel.type === "text" ? (
                        <>
                            <input
                                className="res-filterInput"
                                value={columnFilters[filterModel.key] || ""}
                                onChange={(e) => setColumnFilters((prev) => ({ ...prev, [filterModel.key]: e.target.value }))}
                                placeholder={reservationsCopy.textFilterPlaceholder}
                                autoFocus
                            />
                            <div className="res-filterHint">{reservationsCopy.textFilterHint}</div>
                        </>
                    ) : (
                        <>
                            <input
                                className="res-filterInput"
                                value={filterOptionQuery}
                                onChange={(e) => setFilterOptionQuery(e.target.value)}
                                placeholder={reservationsCopy.searchIn(filterModel.title)}
                                autoFocus
                            />

                            <div className="res-filterList">
                                {visibleFilterOptions.length === 0 ? (
                                    <div className="res-filterHint">{reservationsCopy.noMatchingOptions}</div>
                                ) : (
                                    visibleFilterOptions.map((opt) => {
                                        const selected = Array.isArray(columnFilters[filterModel.key])
                                            ? columnFilters[filterModel.key].includes(opt.value)
                                            : false;

                                        return (
                                            <label key={String(opt.value)} className="res-filterOption" title={opt.label}>
                                                <input
                                                    type="checkbox"
                                                    checked={selected}
                                                    onChange={() => toggleMultiSelectFilter(filterModel.key, opt.value)}
                                                />
                                                <span className="res-filterOptionText">
                                                    <div className="res-filterOptionLabel">{opt.label}</div>
                                                    {opt.meta ? <div className="res-filterOptionMeta">{opt.meta}</div> : null}
                                                </span>
                                                <span className="res-filterCountPill">{opt.count}</span>
                                            </label>
                                        );
                                    })
                                )}
                            </div>

                            <div className="res-filterHint">{reservationsCopy.selectValuesHint}</div>
                        </>
                    )}
                </div>
            </div>
        ) : null}

        {!canRenderCurrentView && (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
            <img
            src={palantirEye}
            alt="Palantir Eye"
            style={{ width: "400px", opacity: 0.3 }}
            />
        </div>
        )}
        </div>
    );
}

export default Reservations;
